import { useMemo } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { useConfig } from '../defaultConfig';
import type { Account, PaymentItem, SalaryInfoResponse } from '../generated-types';
import { MyQueryResult } from '../models/query/types';
import { useAccountNumber } from './useAccount';
import useFuturePayments from './useFuturePayments';
import useSalaryInfo from './useSalaryInfo';

export const useNextSalaryQuery = (): MyQueryResult<
    [SalaryInfoResponse, Account, PaymentItem[]],
    RequestExceptionWithResponse
> => {
    const { featureToggles } = useConfig();
    const salaryInfoQuery = useSalaryInfo(!featureToggles.disableNextSalary);
    const accountNumberQuery = useAccountNumber(salaryInfoQuery.data?.accountNumber);
    const futurePaymentsQuery = useFuturePayments(accountNumberQuery.data?.accountNumber);

    const data = useMemo((): [SalaryInfoResponse, Account, PaymentItem[]] | undefined => {
        if (
            featureToggles.disableNextSalary ||
            !salaryInfoQuery.data ||
            !accountNumberQuery.data ||
            !futurePaymentsQuery.data
        ) {
            return undefined;
        }
        return [salaryInfoQuery.data, accountNumberQuery.data, futurePaymentsQuery.data];
    }, [featureToggles.disableNextSalary, salaryInfoQuery.data, accountNumberQuery.data, futurePaymentsQuery.data]);

    const error = salaryInfoQuery.error || accountNumberQuery.error || futurePaymentsQuery.error;
    const isLoading = salaryInfoQuery.isLoading || accountNumberQuery.isLoading || futurePaymentsQuery.isLoading;

    if (error) {
        return { error, data: undefined, status: 'error' };
    }

    if (isLoading) {
        return { error: null, data: undefined, status: 'pending' };
    }

    if (!data) {
        return { error: null, data: undefined, status: 'suspended' };
    }

    return {
        error: null,
        data,
        status: 'success',
    };
};

export default useNextSalaryQuery;
